import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import AddIcons from '@/icons/AddIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, FormItem, GridHookForm, IconButton } from 'vnetwork-ui'
import { createHoliday } from '../../services'
import { HolidayNewPayload } from '../../types/payload'
import { HOLIDAY_OPTIONS, WORK_N_DAY_TYPES } from '../../types/utils'
import { holidaySchema } from './schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface HolidayFormData {
    data: HolidayNewPayload[]
}

const HolidayCreate: React.FC<IPermissionProps> = () => {
    // useOpenTour()

    const { t } = useTranslation()

    const defaultValues: HolidayFormData = useMemo(
        () => ({
            data: [
                {
                    holiday_date: dayjs().format('YYYY-MM-DD'),
                    holiday_name: HOLIDAY_OPTIONS(t)[0]?.value || '',
                    holiday_work_n_day: WORK_N_DAY_TYPES(t)[2]?.value || 2,
                },
            ],
        }),
        [t]
    )

    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'holiday-list',
    })
    const formControl = useForm<HolidayFormData, any, HolidayFormData>({
        defaultValues,
        // @ts-ignore - temporary ignore for resolver type conflict
        resolver: yupResolver(holidaySchema),
    })
    const { control, watch } = formControl

    useFormPersistence(formControl, 'holiday', defaultValues)

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'data',
    })

    const watchedData = watch('data')

    // Check if first date is Saturday (day 6 in dayjs) and has valid Saturday option
    const isFirstDateSaturday = () => {
        if (watchedData && watchedData[0] && watchedData[0].holiday_date) {
            const isSaturday = dayjs(watchedData[0].holiday_date).day() === 6
            const validOptions = ['Lịch nghỉ thứ 7', 'Lịch làm việc remote']
            const hasValidOption = validOptions.includes(
                watchedData[0].holiday_name
            )
            return isSaturday && hasValidOption
        }
        return false
    }

    // Check if Saturday holidays have been generated (more than one Saturday in the list)
    const hasSaturdayHolidays = () => {
        if (!watchedData || watchedData.length <= 1) return false

        // Check if we have multiple Saturdays
        const saturdays = watchedData.filter(
            (item) => item.holiday_date && dayjs(item.holiday_date).day() === 6
        )
        return saturdays.length > 1
    }

    // Generate all Saturdays in the month starting from first date
    const generateSaturdayHolidays = () => {
        if (!watchedData || !watchedData[0] || !watchedData[0].holiday_date)
            return

        const firstDate = dayjs(watchedData[0].holiday_date)
        const firstHolidayName = watchedData[0].holiday_name
        const endOfMonth = firstDate.endOf('month')

        // Find all Saturdays in the month starting from the first date
        const saturdays = []
        let currentSaturday = firstDate

        while (currentSaturday.isSame(endOfMonth, 'month')) {
            saturdays.push(currentSaturday)
            currentSaturday = currentSaturday.add(7, 'day')
        }

        // Clear existing data and add new Saturday holidays
        // Keep only the first entry and remove others
        while (fields.length > 1) {
            remove(fields.length - 1)
        }

        // Add remaining Saturdays with alternating schedule types based on first option
        for (let i = 1; i < saturdays.length; i++) {
            let holidayName
            if (firstHolidayName === 'Lịch nghỉ thứ 7') {
                // If first is "Lịch nghỉ thứ 7", alternate: remote, nghỉ, remote, nghỉ...
                holidayName =
                    i % 2 === 1 ? 'Lịch làm việc remote' : 'Lịch nghỉ thứ 7'
            } else {
                // If first is "Lịch làm việc remote", alternate: nghỉ, remote, nghỉ, remote...
                holidayName =
                    i % 2 === 1 ? 'Lịch nghỉ thứ 7' : 'Lịch làm việc remote'
            }

            append({
                holiday_date: saturdays[i].format('YYYY-MM-DD'),
                holiday_name: holidayName,
                holiday_work_n_day: 2, // all-day
            })
        }

        toast.success(
            t('auto-create-saturday-success', { count: saturdays.length })
        )
    }

    // Generate all Saturdays in the year starting from first date
    const generateYearSaturdayHolidays = () => {
        if (!watchedData || !watchedData[0] || !watchedData[0].holiday_date)
            return

        const firstDate = dayjs(watchedData[0].holiday_date)
        const firstHolidayName = watchedData[0].holiday_name
        const endOfYear = firstDate.endOf('year')

        // Find all Saturdays in the year starting from the first date
        const saturdays = []
        let currentSaturday = firstDate

        while (currentSaturday.isSameOrBefore(endOfYear, 'day')) {
            saturdays.push(currentSaturday)
            currentSaturday = currentSaturday.add(7, 'day')
        }

        // Clear existing data and add new Saturday holidays
        // Keep only the first entry and remove others
        while (fields.length > 1) {
            remove(fields.length - 1)
        }

        // Add remaining Saturdays with alternating schedule types based on first option
        for (let i = 1; i < saturdays.length; i++) {
            let holidayName
            if (firstHolidayName === 'Lịch nghỉ thứ 7') {
                // If first is "Lịch nghỉ thứ 7", alternate: remote, nghỉ, remote, nghỉ...
                holidayName =
                    i % 2 === 1 ? 'Lịch làm việc remote' : 'Lịch nghỉ thứ 7'
            } else {
                // If first is "Lịch làm việc remote", alternate: nghỉ, remote, nghỉ, remote...
                holidayName =
                    i % 2 === 1 ? 'Lịch nghỉ thứ 7' : 'Lịch làm việc remote'
            }

            append({
                holiday_date: saturdays[i].format('YYYY-MM-DD'),
                holiday_name: holidayName,
                holiday_work_n_day: 2, // all-day
            })
        }

        toast.success(
            t('auto-create-saturday-year-success', { count: saturdays.length })
        )
    }

    const navigate = useNavigate()
    const { mutateAsync: createHolidayMutation, isPending } =
        useMutationWithHandler(
            {
                mutationFn: createHoliday,
            },
            {
                reactQueryKeys: QUERY_KEYS.HOLIDAYS,
                showSuccessToast: false,
                showErrorToast: true,
            }
        )
    const onSubmit: any = async (values: { data: HolidayNewPayload[] }) => {
        try {
            const holidayValues = values.data.map((item) => ({
                holiday_name: item.holiday_name,
                holiday_date: dayjs(item.holiday_date)
                    .startOf('day')
                    .format('YYYY-MM-DDTHH:mm:ssZ'),
                holiday_work_n_day: item.holiday_work_n_day,
            }))

            for (const payload of holidayValues) {
                await createHolidayMutation(payload)
            }
            console.log(holidayValues)
            toast.success(t('create-success'))
            refetchCurrent()
            navigate('/workdays/holiday')
        } catch {
            console.error('Có lỗi xảy ra, vui lòng thử lại')
        }
    }

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.workdayHoliday,
                        title: t('holiday-list'),
                    },
                    {
                        path: paths.workdayHolidayCreate,
                        title: t('holiday-create'),
                    },
                ]}
            />
            <Paper className="mt-4">
                <GridHookForm
                    formControl={formControl}
                    head={{
                        title: t('holiday-choose'),
                        loadingAction: isPending,
                    }}
                    data={[
                        {
                            className: 'col-span-4 mt-4',
                            renderEditComponent: (control) => (
                                <div className="grid gap-4">
                                    {fields.map((item, index) => (
                                        <div
                                            key={item.id}
                                            className="grid lg:grid-cols-12 xs:grid-cols-1 gap-4 items-center"
                                        >
                                            <FormItem
                                                className="md:col-span-4 xs:col-span-1"
                                                control={control}
                                                name={`data.${index}.holiday_date`}
                                                type="datepicker"
                                                label={
                                                    index === 0
                                                        ? t('holiday-date')
                                                        : ''
                                                }
                                            />
                                            <FormItem
                                                className="md:col-span-4 xs:col-span-1"
                                                control={control}
                                                name={`data.${index}.holiday_work_n_day`}
                                                type="radiogroup"
                                                direction="row"
                                                options={WORK_N_DAY_TYPES(t)}
                                                label={t('work-type')}
                                            />

                                            <div className="col-span-2">
                                                <FormItem
                                                    control={control}
                                                    name={`data.${index}.holiday_name`}
                                                    type="select"
                                                    options={HOLIDAY_OPTIONS(t)}
                                                    label={
                                                        index === 0
                                                            ? t('holiday-type')
                                                            : ''
                                                    }
                                                />
                                            </div>

                                            {fields.length > 1 && (
                                                <div className="col-span-1 flex justify-center items-center">
                                                    <IconButton
                                                        size="small"
                                                        className="hover:text-c-error text-secondary"
                                                        onClick={() =>
                                                            remove(index)
                                                        }
                                                    >
                                                        <DeleteOutlineIcons />
                                                    </IconButton>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Action buttons */}
                                    <div className="flex gap-2 flex-wrap">
                                        <Button
                                            size="small"
                                            type="button"
                                            endIcon={<AddIcons />}
                                            variant="outlined"
                                            disabled={isPending}
                                            onClick={() =>
                                                append({
                                                    holiday_date: dayjs(
                                                        fields[
                                                            fields.length - 1
                                                        ]?.holiday_date ||
                                                            dayjs()
                                                    )
                                                        .add(1, 'day')
                                                        .format('YYYY-MM-DD'),
                                                    holiday_name:
                                                        HOLIDAY_OPTIONS(t)[0]
                                                            ?.value || '',
                                                    holiday_work_n_day:
                                                        WORK_N_DAY_TYPES(t)[2]
                                                            ?.value || 2,
                                                })
                                            }
                                        >
                                            {t('add-day')}
                                        </Button>

                                        <Button
                                            size="small"
                                            type="button"
                                            variant="outlined"
                                            disabled={
                                                !isFirstDateSaturday() ||
                                                hasSaturdayHolidays() ||
                                                isPending
                                            }
                                            onClick={generateSaturdayHolidays}
                                            className="bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-300"
                                        >
                                            {t('auto-create-saturday')}
                                        </Button>

                                        <Button
                                            size="small"
                                            type="button"
                                            variant="outlined"
                                            disabled={
                                                !isFirstDateSaturday() ||
                                                hasSaturdayHolidays() ||
                                                isPending
                                            }
                                            onClick={
                                                generateYearSaturdayHolidays
                                            }
                                            className="bg-green-50 text-green-600 border-green-300 hover:bg-green-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-300"
                                        >
                                            {t('auto-create-saturday-year')}
                                        </Button>
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                    actions={[
                        {
                            title: t('cancel-operation'),
                            type: 'button',
                            variant: 'outlined',
                            handler: () => navigate(paths.workdayHoliday),
                        },
                        {
                            title: t('holiday-create'),
                            type: 'submit',
                            variant: 'contained',
                            loading: isPending,
                            disabled: isPending,
                        },
                    ]}
                    onSubmit={onSubmit}
                />
            </Paper>
        </div>
    )
}

export default HolidayCreate
